import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { computed, nextTick, onBeforeMount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import EnFilterButtonBase from 'Components/datagrid/filterButtons/EnFilterButtonBase';
import EnFilterTypeSelectButton from 'Components/datagrid/filterButtons/EnFilterTypeSelectButton';
import EnDatePicker from 'Components/inputs/EnDatePicker';
import EnAutocomplete from 'Components/inputs/EnAutocomplete/EnAutocomplete.vue';
import EnTextField from 'Components/inputs/EnTextField/EnTextField.vue';
import { dateFormatDay } from 'Js/utils/dateFormat';
import { FilterEvent } from '../types/filterEvent';
import { useResetFilter } from '../composables/useFilterReset';
import { FilterButtonType } from '../types/filterButtonType';
import { FilterType } from 'Js/enums/filterType.js';
import VarType from 'Js/enums/varType';
import { useFilter } from '../composables/filterButtonComposable';
import EnFilterButtonTypePrefix from 'Components/datagrid/filterButtons/EnFilterButtonTypePrefix.vue';
/**
 * A filter button for a multiple values.
 */
export default /*@__PURE__*/ _defineComponent({
    __name: 'EnMultipleSearchFilterButton',
    props: /*@__PURE__*/ _mergeModels({
        type: String,
        initialType: {
            type: String,
            default: FilterType.Equal,
        },
        hint: String,
        label: String,
        filterTypeSelectionVisible: Boolean,
        applyButtonText: String,
        resetButtonText: String,
        noFilterText: String,
        id: String,
        displaySeparator: String,
        hideMenuTitle: Boolean,
    }, {
        "modelValue": {},
        "modelModifiers": {},
        "openOnRender": {},
        "openOnRenderModifiers": {},
    }),
    emits: /*@__PURE__*/ _mergeModels(['update:modelValue', FilterEvent.Input, 'update:type', 'update:openOnRender', FilterEvent.FilterUpdated, FilterEvent.FilterReset], ["update:modelValue", "update:openOnRender"]),
    setup(__props, { expose: __expose, emit: __emit }) {
        const componentProps = __props;
        const { t } = useI18n();
        const modelValue = _useModel(__props, 'modelValue');
        const openOnRender = _useModel(__props, 'openOnRender');
        const emit = __emit;
        const { resetFilter } = useResetFilter(emit);
        const menuIsVisible = ref(openOnRender.value);
        watch(menuIsVisible, (newValue) => {
            emit('update:openOnRender', newValue);
        });
        const { 
        // Data
        displayString, changedValue, selectedType, isSingleFilter, 
        // Computed
        noFilterTextComputed, resetButtonTextComputed, applyButtonTextComputed, 
        // Methods
        typeItemsNumber, shouldShowPrefixMethod, typeItemsWithLikeString, typeItemsString, returnTypeId, returnId, returnFilterButtonId, onResetButtonClicked, applyFilters, getDisplayString, emitUpdate, } = useFilter(componentProps, { emit }, modelValue, updateDisplayString);
        const fieldKeys = computed(() => Object.keys(modelValue.value));
        const displayStrings = ref([]);
        function updateDisplayString(fieldDefinitions) {
            let displayStringArray = [];
            fieldKeys.value.forEach(fieldKey => {
                const value = getDisplayValue(fieldDefinitions[fieldKey]);
                if (value)
                    displayStringArray.push(getDisplayValue(fieldDefinitions[fieldKey]));
            });
            const connectorSymbol = componentProps.displaySeparator
                ?? (Object.values(fieldDefinitions).every(field => field.filterButtonType === FilterButtonType.Date) ? '-' : ' ');
            displayStrings.value = displayStringArray;
            displayString.value = displayStringArray.join(connectorSymbol).trim() || noFilterTextComputed.value;
        }
        const anyValueIsSet = computed(() => {
            return displayString.value !== noFilterTextComputed.value;
        });
        function getDisplayValue(filter) {
            const fieldValue = filter.value;
            if (filter && Boolean(fieldValue)) {
                if (filter.filterButtonType === FilterButtonType.Date) {
                    return dateFormatDay(new Date(fieldValue));
                }
                else {
                    return fieldValue;
                }
            }
            return undefined;
        }
        function actualTypeItems(property) {
            if (property.filterButtonType === FilterButtonType.Date || property.filterButtonType === FilterButtonType.Number) {
                return typeItemsNumber;
            }
            return property.allowLike ? typeItemsWithLikeString : typeItemsString;
        }
        function shouldShowFilterTypeSelection(fieldKey) {
            return componentProps.filterTypeSelectionVisible && changedValue.value[fieldKey].filterButtonType !== FilterButtonType.Autocomplete;
        }
        function getInputId(fieldKey) {
            return changedValue.value[fieldKey].inputId ?? fieldKey;
        }
        function getAutocompletePreselectedObject(fieldKey) {
            const field = changedValue.value[fieldKey];
            if (!field?.value || typeof field.value === VarType.Object) {
                return field?.value;
            }
            // Autocomplete needs an object to display an already-selected primitive value
            // before that value is available in the loaded items list.
            const selectedObject = {
                [field.itemValue ?? field.dataField]: field.value,
            };
            // String display expressions can be mirrored onto the placeholder object.
            // Function display expressions need the real object, so they are left untouched.
            if (typeof field.displayExpression === VarType.String) {
                selectedObject[field.displayExpression] = field.value;
            }
            return selectedObject;
        }
        function updateFieldValue(fieldKey, value) {
            changedValue.value[fieldKey].value = value;
            if (fieldKey !== 'fullValue' && value && changedValue.value.fullValue?.value) {
                changedValue.value.fullValue.value = null;
            }
        }
        async function applyAutocompleteFilterSelection() {
            await nextTick();
            applyFilters();
        }
        onBeforeMount(() => {
            isSingleFilter.value = false;
            updateDisplayString(modelValue.value);
        });
        __expose({
            updateDisplayString
        });
        const __returned__ = { componentProps, t, modelValue, openOnRender, emit, resetFilter, menuIsVisible, displayString, changedValue, selectedType, isSingleFilter, noFilterTextComputed, resetButtonTextComputed, applyButtonTextComputed, typeItemsNumber, shouldShowPrefixMethod, typeItemsWithLikeString, typeItemsString, returnTypeId, returnId, returnFilterButtonId, onResetButtonClicked, applyFilters, getDisplayString, emitUpdate, fieldKeys, displayStrings, updateDisplayString, anyValueIsSet, getDisplayValue, actualTypeItems, shouldShowFilterTypeSelection, getInputId, getAutocompletePreselectedObject, updateFieldValue, applyAutocompleteFilterSelection, get EnFilterButtonBase() { return EnFilterButtonBase; }, get EnFilterTypeSelectButton() { return EnFilterTypeSelectButton; }, get EnDatePicker() { return EnDatePicker; }, EnAutocomplete, EnTextField, get FilterButtonType() { return FilterButtonType; }, EnFilterButtonTypePrefix };
        Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true });
        return __returned__;
    }
});
